/**
 * utils/naluTracking.js
 *
 * Nalu - GTM tracking helper.
 * This pushes Nalu-only events into `window.dataLayer` so GTM can forward them to GA4.
 *
 * IMPORTANT: Event names should stay prefixed with `nalu_` to avoid collisions.
 */

const DATA_LAYER_NAME = "dataLayer";

/* Ensure window.dataLayer exists (browser only). */
function getDataLayer() {
  if (typeof window === "undefined") return null;

  // Create if missing
  window[DATA_LAYER_NAME] = window[DATA_LAYER_NAME] || [];
  return window[DATA_LAYER_NAME];
}

/**
 * Push an event to GTM dataLayer.
 *
 * @param {string} eventName - e.g. "nalu_modal_open"
 * @param {Record<string, any>} params - event parameters
 * @returns {boolean} true if pushed, false if not (e.g., SSR)
 */
export function naluTrack(eventName, params = {}) {
  // Block tracking unless explicitly enabled (production only)
  if (!window?.naluBookingVars?.analyticsEnabled) return false;

  const dl = getDataLayer();
  if (!dl) return false;

  if (typeof eventName !== "string" || !eventName.trim()) return false;

  const payload = { event: eventName, ...params };

  // Debug logging controlled from wp-config.php
  if (window?.naluBookingVars?.trackDebug) {
    console.log("[NALU TRACK]", payload);
  }

  dl.push(payload);
  return true;
}

/**
 * Convenience wrappers (optional, but keeps BookingForm tidy)
 */
export const naluEvents = {
  modalOpen:          (params = {}) => naluTrack("nalu_modal_open", params),
  modalClose:         (params = {}) => naluTrack("nalu_modal_close", params),
  stepView:           (params = {}) => naluTrack("nalu_step_view", params),
  stepComplete:       (params = {}) => naluTrack("nalu_step_complete", params),
  bookingConfirmed:   (params = {}) => naluTrack("nalu_booking_confirmed", params),
};
